import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { MipMaterialClassificationDTO, TruckRecordingMipService } from '../generated';
import { MaterialClassification, transformMaterialClassification } from './materialClassification';
import { camelToSnake } from '../resources/materialClassification.constants';

export interface TruckRecordingMipMaterialClassificationOptionsProps {
  jobsiteId?: string;
  mipReportId?: string;
  enabled?: boolean;
}

export function truckRecordingMipMaterialClassificationOptions({
  jobsiteId,
  mipReportId,
  enabled = true,
}: TruckRecordingMipMaterialClassificationOptionsProps) {
  return queryOptions<MaterialClassification, ApiError>({
    queryKey: ['truckRecordingMipMaterialClassification', jobsiteId, mipReportId],
    queryFn: async () => {
      if (!jobsiteId || !mipReportId) {
        throw new Error('jobsiteId and mipReportId are required');
      }

      const response = await TruckRecordingMipService.truckRecordingMipControllerGetMaterialClassification({
        path: { jobsiteId, mipReportId },
      });

      if (response.data !== undefined) {
        return transformMipMaterialClassification(response.data);
      }

      throw (response as any).error || new Error(response.response?.statusText || 'Unknown error');
    },
    enabled: !!jobsiteId && !!mipReportId && !!enabled,
  });
}

export function useTruckRecordingMipMaterialClassification(props: TruckRecordingMipMaterialClassificationOptionsProps) {
  return useQuery<MaterialClassification, ApiError>(truckRecordingMipMaterialClassificationOptions(props));
}

function transformMipMaterialClassification(resp: MipMaterialClassificationDTO): MaterialClassification {
  const { ewcMapping, ...classes } = resp.data;
  const output = (Object.entries(classes) as Array<[string, number | undefined]>)
    .filter((entry): entry is [string, number] => entry[1] !== undefined)
    .map(([className, probability]) => ({
      class_name: camelToSnake(className),
      avv: ewcMapping?.[className as keyof typeof ewcMapping] ?? '',
      probability,
    }));
  return transformMaterialClassification(output);
}
